/**
 * "Assign to" — the console's version of the phone's assignee page.
 *
 * ## What it is for
 *
 * An assignee that the AI chose is a guess, and the phone has always let a
 * human inspect the guess: tapping the assignee field opens a ranked list of
 * everybody the matcher considered, each with the calibrated confidence
 * behind them, above a directory search for when it got it wrong. The
 * console showed none of that — its picker offered search and nothing else —
 * so the same record explained itself on site and went silent at the desk
 * (user, 2026-09-11: "follow the same UX UI as mobile specifically as it
 * pertains to what happens when you tap on the assignee field").
 *
 * This is that page, on the console's paper. Every DECISION it renders comes
 * from the phone's own modules through `assigneeSuggestion.ts` — which
 * affordance is live, what the bars read, what the banner says — so the two
 * clients cannot come to different conclusions about the same record. What
 * is written here is the drawing, and only the drawing.
 *
 * ## Two deliberate departures from the phone
 *
 * **A modal, not a full-screen page.** The phone takes the whole screen
 * because it has no room beside the field. A console that blanked the record
 * you are reviewing in order to name somebody would lose the context the
 * choice is made from, so the sheet floats over it (user: "we should do this
 * in a modal").
 *
 * **Choosing is a DRAFT; Done commits it.** On the phone, tapping a person
 * persists them and closes the page in one gesture — fine on a handset, where
 * the page is the whole screen and reopening it is one tap. On a console it
 * made the sheet impossible to browse: the first row you touched to compare
 * against was the one you assigned, and the sheet was gone before you could
 * look at the next (user, 2026-09-11: "allow me to choose a user and put a
 * selection rounded rect around that item without actually making the choice
 * ... when I press done, it takes whoever is selected currently").
 *
 * So every change here — a candidate, a directory result, Clear — moves a
 * local selection and nothing else. `Done` applies it, `Cancel` (and ✕, and
 * Escape, and the scrim) throws it away. Which makes Done's counterpart worth
 * a button again: the phone's Cancel is back, because it now decides
 * something.
 *
 * The pending choice has to be legible from anywhere in the sheet, because a
 * directory selection can be scrolled past or typed out of view. The BANNER
 * carries it — the one element that is always at the top and already changes
 * with state.
 *
 * ## Who opens it
 *
 * The “Review suggested matches” action in `AssigneePicker` opens this sheet
 * when ranked candidates exist. The field itself always opens a combobox.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ConsoleAssigneeCandidate, ConsoleAssigneeSuggestion, ItemEditDirectoryOption } from "../api";
import { C, FONT_MONO } from "../tokens";
import { initialsOf, META } from "./chrome";
import { NO_AUTOFILL } from "../lib/noAutofill";
import { bannerCopy, rankedCandidates } from "./assigneeSuggestion";
import { AssigneeSourceBadge } from "../../../../backend/src/frontend/components/shared/assignee/AssigneeSourceBadge";
import { assigneeSourceFor } from "../../../../backend/src/frontend/components/shared/assignee/assigneeIdentity";

export interface AssignToModalProps {
  /** The record's kind, for the header's eyebrow ("PUNCH · ASSIGNEE"). */
  typeLabel?: string;
  label: string;
  suggestion?: ConsoleAssigneeSuggestion;
  /** The assignee as it stands — empty when nobody is set. */
  value: string;
  /** Directory search, owned by the caller so the modal and the anchored
   *  panel share one query path. */
  query: string;
  onQueryChange: (next: string) => void;
  options: ItemEditDirectoryOption[];
  isLoading: boolean;
  failed: boolean;
  /**
   * The directory id currently ON the record, when one is known.
   *
   * Used to mark which row is already assigned. Falls back to matching by
   * name, because the stored `selectedAssigneeRemoteId` may be in a different
   * one of Procore's three id spaces than the candidates carry.
   */
  committedRemoteId?: string;
  /** Applied when Done is pressed, and only then. `null` clears the
   *  assignee; the modal closes itself afterwards. */
  multiple?: { selected: ItemEditDirectoryOption[] };
  onCommit: (choice: ItemEditDirectoryOption | null, selected?: ItemEditDirectoryOption[]) => void | boolean | Promise<void | boolean>;
  onClose: () => void;
  testId?: string;
}

/**
 * The pending selection.
 *
 * `undefined` — untouched, so whatever is on the record stands.
 * `null`      — Clear was pressed; Done will remove the assignee.
 * an option   — Done will assign that person.
 */
type Draft = ItemEditDirectoryOption | null | undefined;

export function AssignToModal({
  typeLabel,
  label,
  suggestion,
  value,
  query,
  onQueryChange,
  options,
  isLoading,
  failed,
  committedRemoteId,
  onCommit,
  multiple,
  onClose,
  testId = "assign-to",
}: AssignToModalProps) {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const ranked = rankedCandidates(suggestion);
  const trimmed = query.trim();
  const [draft, setDraft] = useState<Draft>(undefined);
  const [multiOn, setMultiOn] = useState((multiple?.selected.length ?? 0) > 1);
  const [picked, setPicked] = useState<ItemEditDirectoryOption[]>(multiple?.selected ?? []);
  const [selectionChanged, setSelectionChanged] = useState(false);
  // A directory may arrive after opening, mapping a provider id to a teammate.
  // Canonicalize identities without replacing the user's pending selection.
  useEffect(() => {
    if (!multiple) return;
    setPicked(current => current.map(person => options.find(option => option.remoteId === person.remoteId || option.providerRemoteIds?.includes(person.remoteId)) ?? person)
      .filter((person, index, all) => all.findIndex(other => other.remoteId === person.remoteId) === index));
  }, [options]);
  const choose = (person: ItemEditDirectoryOption) => {
    if (!multiple) { setDraft(person); return; }
    setSelectionChanged(true);
    setPicked(current => multiOn
      ? current.some(entry => entry.remoteId === person.remoteId)
        ? current.filter(entry => entry.remoteId !== person.remoteId) : [...current, person]
      : [person]);
  };

  // Canonical ids distinguish people even when they share a display name.
  // A name is only a fallback for old records without a selected identity.
  const isCommitted = (remoteId: string, name: string) =>
    !!value && (committedRemoteId ? remoteId === committedRemoteId : name === value);
  /** Is this row the one the selection rect sits on? Untouched, that is
   *  whoever is already assigned; after a pick it is the pick; after Clear it
   *  is nobody. */
  const isSelected = (remoteId: string, name: string) =>
    multiple ? picked.some(person => person.remoteId === remoteId) :
    draft === null ? false : draft ? remoteId === draft.remoteId : isCommitted(remoteId, name);

  const changed =
    draft !== undefined &&
    (draft === null
      ? !!value
      : !isCommitted(draft.remoteId, draft.name));
  // A pending choice states itself where it cannot be scrolled away from.
  const banner = multiple && selectionChanged ? { title: "Selected", sub: picked.length ? `${picked.map(person => person.name).join(", ")} — press Done to assign.` : "Nobody — press Done to remove the assignees." } : changed
    ? {
        title: "Selected",
        sub: draft
          ? `${draft.name} — press Done to assign.`
          : "Nobody — press Done to remove the assignee.",
      }
    : bannerCopy(suggestion);
  /** Nothing to remove when nobody is effectively chosen. */
  const clearable = multiple ? picked.length > 0 : draft === null ? false : draft ? true : !!value;

  const commit = async () => {
    const result = multiple && selectionChanged ? await onCommit(picked[0] ?? null, picked)
      : !multiple && changed ? await onCommit(draft === null ? null : draft!) : undefined;
    if (result !== false) onClose();
  };

  // Focus the sheet itself rather than the search box. The candidates are the
  // reason this opened; a cursor blinking in a field below them says the
  // opposite, and on the phone the keyboard is deliberately left down for the
  // same reason.
  useEffect(() => {
    sheetRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  return createPortal(
    <div
      data-testid={`${testId}-scrim`}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        // Above `AnchoredPanel` (60), which is what this replaces.
        zIndex: 80,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 28,
        background: C.scrim,
      }}
    >
      <div
        ref={sheetRef}
        data-testid={testId}
        role="dialog"
        aria-modal="true"
        aria-label={`Assign ${label.toLocaleLowerCase()}`}
        tabIndex={-1}
        className="pop"
        style={{
          width: "100%",
          maxWidth: 468,
          maxHeight: "100%",
          display: "flex",
          flexDirection: "column",
          outline: "none",
          background: C.surface,
          borderRadius: 20,
          border: `1px solid ${C.line}`,
          boxShadow: `0 30px 70px -20px ${C.line2}`,
        }}
      >
        <Header
          typeLabel={typeLabel}
          label={label}
          onClose={onClose}
          testId={testId}
        />

        {multiple && <label style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 20px", borderBottom: `1px solid ${C.line}`, cursor: "pointer" }}>
          <span style={{ fontSize: 14, fontWeight: 600 }}>Select multiple</span>
          <span style={{ position: "relative", width: 40, height: 24, borderRadius: 999, background: multiOn ? C.ink : C.line2 }}>
          <input type="checkbox" role="switch" aria-label="Select multiple" checked={multiOn}
            onChange={event => {
              const enabled = event.target.checked;
              setMultiOn(enabled);
              if (!enabled && picked.length > 1) { setPicked(picked.slice(0, 1)); setSelectionChanged(true); }
            }} style={{ position: "absolute", inset: 0, margin: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer", zIndex: 1 }} />
            <span aria-hidden style={{ position: "absolute", top: 3, left: multiOn ? 19 : 3, width: 18, height: 18, borderRadius: "50%", background: C.surface, transition: "left .18s ease" }} />
          </span>
        </label>}
        <div className="sb" style={{ overflowY: "auto", padding: "14px 20px 18px" }}>
          <Banner title={banner.title} sub={banner.sub} testId={testId} />

          {ranked.length > 0 && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "16px 2px 9px",
                }}
              >
                <Sparkle />
                <span
                  data-testid={`${testId}-count`}
                  style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink }}
                >
                  {ranked.length} likely {ranked.length === 1 ? "assignee" : "assignees"}
                </span>
              </div>
              {ranked.map(({ candidate, pct, lead }) => (
                <CandidateRow
                  key={candidate.remoteId}
                  candidate={candidate}
                  pct={pct}
                  lead={lead}
                  committed={isCommitted(candidate.remoteId, candidate.name)}
                  selected={isSelected(candidate.remoteId, candidate.name)}
                  testId={testId}
                  onSelect={() =>
                    choose({
                      remoteId: candidate.remoteId,
                      name: candidate.name,
                      source: assigneeSourceFor(candidate.remoteId, candidate.source, "procore"),
                      ...(candidate.company ? { company: candidate.company } : {}),
                      ...(candidate.jobTitle ? { jobTitle: candidate.jobTitle } : {}),
                    })
                  }
                />
              ))}
            </>
          )}

          <div
            style={{
              ...META,
              fontSize: 10.93,
              letterSpacing: "1.2px",
              color: C.sub2,
              padding: `${ranked.length > 0 ? 18 : 16}px 2px 8px`,
            }}
          >
            {ranked.length > 0 ? "Someone else?" : "Search people"}
          </div>
          <SearchBox
            query={query}
            onQueryChange={onQueryChange}
            testId={testId}
          />
          <DirectoryResults
            trimmed={trimmed}
            options={options}
            isLoading={isLoading}
            failed={failed}
            isSelected={(option) => isSelected(option.remoteId, option.name)}
            onPick={choose}
            testId={testId}
          />
        </div>

        <div
          style={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            padding: "12px 20px",
            borderTop: `1px solid ${C.hairRow}`,
            background: C.inset,
            borderRadius: "0 0 20px 20px",
          }}
        >
          <button
            type="button"
            className="kb"
            data-testid={`${testId}-clear`}
            disabled={!clearable}
            onClick={() => { setDraft(null); setPicked([]); setSelectionChanged(true); }}
            style={{
              height: 36,
              padding: "0 14px",
              borderRadius: 10,
              border: `1px solid ${clearable ? C.line2 : C.hairRow}`,
              background: C.surface,
              color: clearable ? C.ink : C.sub2,
              font: "inherit",
              fontSize: 14.95,
              fontWeight: 600,
              cursor: clearable ? "pointer" : "default",
            }}
          >
            Clear assignee
          </button>
          <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* Text, not an outline: an outlined Cancel beside an outlined
                Clear reads as two equal actions, and only one of them does
                anything to the record. */}
            <button
              type="button"
              className="kb"
              data-testid={`${testId}-cancel`}
              onClick={onClose}
              style={{
                height: 36,
                padding: "0 12px",
                borderRadius: 10,
                border: "none",
                background: "transparent",
                color: C.sub,
                font: "inherit",
                fontSize: 14.95,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="kb"
              data-testid={`${testId}-done`}
              onClick={commit}
              style={{
                height: 36,
                padding: "0 20px",
                borderRadius: 10,
                border: "none",
                background: C.ink,
                color: C.surface,
                font: "inherit",
                fontSize: 14.95,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {multiOn ? `Done · ${picked.length}` : "Done"}
            </button>
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Header({
  typeLabel,
  label,
  onClose,
  testId,
}: {
  typeLabel?: string;
  label: string;
  onClose: () => void;
  testId: string;
}) {
  return (
    <div
      style={{
        flex: "none",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "18px 20px 14px",
        borderBottom: `1px solid ${C.hairRow}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...META, fontSize: 10.93, letterSpacing: "1.3px", color: C.sub2 }}>
          {typeLabel ? `${typeLabel} · ${label}` : label}
        </div>
        <div
          style={{
            marginTop: 3,
            fontSize: 21.85,
            fontWeight: 700,
            letterSpacing: "-0.4px",
            color: C.ink,
          }}
        >
          Assign to
        </div>
      </div>
      <button
        type="button"
        className="kb"
        aria-label="Close"
        data-testid={`${testId}-close`}
        onClick={onClose}
        style={{
          width: 26,
          height: 26,
          flex: "none",
          marginTop: 2,
          borderRadius: "50%",
          border: "none",
          background: C.faint,
          color: C.sub,
          display: "grid",
          placeItems: "center",
          padding: 0,
          font: "inherit",
          fontSize: 19.55,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        ×
      </button>
    </div>
  );
}

function Banner({ title, sub, testId }: { title: string; sub: string; testId: string }) {
  return (
    <div
      data-testid={`${testId}-banner`}
      style={{ background: C.faint, borderRadius: 12, padding: "10px 13px 11px" }}
    >
      <div style={{ ...META, fontSize: 10.93, letterSpacing: "1.1px", color: C.sub }}>
        {title}
      </div>
      <div style={{ marginTop: 3, fontSize: 14.37, lineHeight: 1.45, color: C.ink }}>
        {sub}
      </div>
    </div>
  );
}

/**
 * One ranked person: the avatar, the role, the confidence bar and its number.
 *
 * ## Three states, and they are three different facts
 *
 * `lead` is the MODEL's — a clear leader only when it dominates
 * (`assigneeDistributionPercentages`), so a flat 34/33/33 draws three equal
 * rows rather than crowning the first by a rounding error. It is green, like
 * every other confidence signal on the row.
 *
 * `selected` is YOURS — the rect that follows your pointer around the list
 * and says what Done will take. It is INK, because green already means "the
 * model is confident" and a second green would make the two indistinguishable
 * on the row where they coincide, which is the common one.
 *
 * `committed` is the RECORD's — who is assigned right now. It stays visible
 * as a quiet badge once your selection moves off it, so a reassignment shows
 * what it is changing from rather than only what it is changing to.
 */
function CandidateRow({
  candidate,
  pct,
  lead,
  committed,
  selected,
  onSelect,
  testId,
}: {
  candidate: ConsoleAssigneeCandidate;
  pct: number;
  lead: boolean;
  committed: boolean;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}) {
  const role =
    [candidate.jobTitle, candidate.company || candidate.vendorLabel]
      .filter(Boolean)
      .join(" · ") || "Project directory";
  return (
    <button
      type="button"
      className="kb candrow"
      data-testid={`${testId}-candidate`}
      data-remote-id={candidate.remoteId}
      data-lead={lead ? "true" : undefined}
      data-current={committed ? "true" : undefined}
      data-selected={selected ? "true" : undefined}
      aria-pressed={selected}
      onClick={onSelect}
      style={{
        width: "100%",
        textAlign: "left",
        display: "flex",
        alignItems: "center",
        gap: 11,
        padding: "10px 12px",
        marginBottom: 7,
        borderRadius: 12,
        background: selected ? C.rowSelected : C.surface,
        border: `1.5px solid ${selected ? C.ink : lead ? C.green : C.line}`,
        font: "inherit",
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 34,
          height: 34,
          flex: "none",
          display: "grid",
          placeItems: "center",
          borderRadius: "50%",
          background: C.ink,
          color: C.surface,
          fontSize: 13.8,
          fontWeight: 800,
          letterSpacing: "0.02em",
          boxShadow: lead ? `0 0 0 2px ${C.green}` : "none",
        }}
      >
        {initialsOf(candidate.name)}
      </span>
      <span style={{ flex: 1, minWidth: 0, display: "block" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              fontSize: 16.1,
              fontWeight: 700,
              color: C.ink,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {candidate.name}
          </span>
          <AssigneeSourceBadge source={assigneeSourceFor(candidate.remoteId, candidate.source, "procore")} />
          {/* Never beside SET. When the matcher's first pick is also the
              person currently assigned, two badges say one thing twice — and
              the row's green ring, bar and percentage have already said which
              one leads. TOP earns its place only when the lead and the
              assignment are DIFFERENT people, which is the case worth
              noticing. */}
          {lead && !committed && (
            <span
              style={{
                flex: "none",
                fontFamily: FONT_MONO,
                fontSize: 9.2,
                fontWeight: 700,
                letterSpacing: "0.4px",
                color: C.green,
                background: C.greenSoft,
                padding: "2px 5px",
                borderRadius: 4,
              }}
            >
              TOP
            </span>
          )}
          {committed && (
            <span
              style={{
                flex: "none",
                fontFamily: FONT_MONO,
                fontSize: 9.2,
                fontWeight: 700,
                letterSpacing: "0.4px",
                color: C.goldInk,
                background: C.weekSelected,
                padding: "2px 5px",
                borderRadius: 4,
              }}
            >
              SET
            </span>
          )}
        </span>
        <span
          style={{
            display: "block",
            marginTop: 1,
            marginBottom: 6,
            fontSize: 13.23,
            color: C.sub,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {role}
        </span>
        <span
          aria-hidden
          style={{
            display: "block",
            height: 4,
            borderRadius: 2,
            background: C.track,
            overflow: "hidden",
          }}
        >
          <span
            data-testid={`${testId}-bar`}
            style={{
              display: "block",
              height: "100%",
              width: `${pct}%`,
              borderRadius: 2,
              background: lead ? C.green : C.meterOn,
            }}
          />
        </span>
      </span>
      <span
        style={{
          flex: "none",
          minWidth: 32,
          textAlign: "right",
          fontFamily: FONT_MONO,
          fontSize: 13.8,
          fontWeight: 700,
          color: lead ? C.green : C.sub,
        }}
      >
        {pct}%
      </span>
    </button>
  );
}

function SearchBox({
  query,
  onQueryChange,
  testId,
}: {
  query: string;
  onQueryChange: (next: string) => void;
  testId: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "9px 11px",
        borderRadius: 11,
        border: `1px solid ${C.line2}`,
        background: C.surface,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flex: "none" }}>
        <circle cx="6" cy="6" r="4.4" stroke={C.sub} strokeWidth="1.5" />
        <path d="M9.4 9.4L12.5 12.5" stroke={C.sub} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        data-testid={`${testId}-search`}
        // The same box as the anchored picker's, and it has to behave the
        // same way — see `lib/noAutofill`.
        {...NO_AUTOFILL}
        aria-label="Search directory"
        value={query}
        placeholder="Type a name, company, or email…"
        onInput={(event) => onQueryChange((event.target as HTMLInputElement).value)}
        onChange={(event) => onQueryChange(event.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          color: C.ink,
          font: "inherit",
          fontSize: 15.52,
        }}
      />
    </div>
  );
}

function DirectoryResults({
  trimmed,
  options,
  isLoading,
  failed,
  isSelected,
  onPick,
  testId,
}: {
  trimmed: string;
  options: ItemEditDirectoryOption[];
  isLoading: boolean;
  failed: boolean;
  isSelected: (option: ItemEditDirectoryOption) => boolean;
  onPick: (option: ItemEditDirectoryOption) => void;
  testId: string;
}) {
  // Nothing typed yet is not an empty result — it is the resting state, and a
  // box saying so under an empty field is chrome about chrome.
  if (trimmed.length < 2 && options.length === 0 && !failed && !isLoading) return null;

  const message = isLoading
    ? "Searching eligible assignees…"
    : failed
      ? "Could not load assignees. Try again."
      : options.length === 0
        ? `No eligible assignees match “${trimmed}”.`
        : null;

  return (
    <div
      data-testid={`${testId}-results`}
      style={{
        marginTop: 7,
        borderRadius: 11,
        border: `1px solid ${C.hairRow}`,
        background: C.inset,
        overflow: "hidden",
      }}
    >
      {message ? (
        <div
          style={{
            padding: "11px 12px",
            fontSize: 14.37,
            lineHeight: 1.45,
            color: failed ? C.red : C.sub,
          }}
        >
          {message}
        </div>
      ) : (
        options.map((option, index) => {
          const picked = isSelected(option);
          return (
            <button
              key={`${option.remoteId}:${option.name}`}
              type="button"
              className="kb dirrow"
              role="option"
              aria-selected={picked}
              data-testid={`${testId}-option`}
              data-remote-id={option.remoteId}
              data-selected={picked ? "true" : undefined}
              onClick={() => onPick(option)}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "9px 12px",
                border: "none",
                borderTop: index === 0 || picked ? "none" : `1px solid ${C.hairRow}`,
                background: picked ? C.rowSelected : "transparent",
                // The ranked rows carry a full ink rect; these are a compact
                // list inside one box, so the same claim is made with the ink
                // edge alone — a 1.5px box around a 44px list row would draw
                // a second table.
                boxShadow: picked ? `inset 2px 0 0 ${C.ink}` : "none",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 26,
                  height: 26,
                  flex: "none",
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  background: picked ? C.ink : C.faint,
                  color: picked ? C.surface : C.sub,
                  fontSize: 11.5,
                  fontWeight: 800,
                }}
              >
                {initialsOf(option.name)}
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: 15.52, fontWeight: 700, color: C.ink }}>
                  {option.name}
                </span>
                {(option.company || option.jobTitle || option.email) && (
                  <span style={{ display: "block", marginTop: 1, fontSize: 13.23, color: C.sub }}>
                    {[option.company, option.jobTitle, option.email].filter(Boolean).join(" · ")}
                  </span>
                )}
              </span>
              <AssigneeSourceBadge source={option.source} />
              {picked && (
                <span
                  aria-hidden
                  style={{
                    ...META,
                    flex: "none",
                    fontSize: 9.77,
                    letterSpacing: "0.6px",
                    color: C.ink,
                  }}
                >
                  Selected
                </span>
              )}
            </button>
          );
        })
      )}
    </div>
  );
}

/** The phone marks the ranked list with a sparkle — the one glyph in either
 *  client that says "this part was written by the model". */
function Sparkle() {
  return (
    <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden style={{ flex: "none" }}>
      <path
        d="M7 1l1.5 3.9L12.4 6.4 8.5 7.9 7 11.8 5.5 7.9 1.6 6.4 5.5 4.9 7 1z"
        fill={C.gold}
      />
    </svg>
  );
}
